import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Trash2, X } from "lucide-react";
import { authHeaders } from "./AdminLayout";
import { PORTFOLIO_CATEGORIES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(PORTFOLIO_CATEGORIES[0]);
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => {
    axios
      .get(`${API}/portfolio`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Nu am putut încărca portofoliul."));
  };

  useEffect(load, []);

  const onFile = (e) => {
    const all = Array.from(e.target.files || []);
    if (all.length > 20) toast.warning("Ai selectat peste 20 de poze — am păstrat primele 20.");
    const picked = all.slice(0, 20);
    previews.forEach((u) => URL.revokeObjectURL(u));
    setFiles(picked);
    setPreviews(picked.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const removePreview = (idx) => {
    URL.revokeObjectURL(previews[idx]);
    setFiles(files.filter((_, i) => i !== idx));
    setPreviews(previews.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Alege cel puțin o imagine mai întâi.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("title", title);
      fd.append("category", category);
      fd.append("location", location);
      fd.append("description", description);
      const { data } = await axios.post(`${API}/admin/portfolio/bulk`, fd, { headers: authHeaders() });
      toast.success(
        data.created > 1 ? `${data.created} lucrări adăugate în portofoliu.` : "Lucrare adăugată în portofoliu."
      );
      previews.forEach((u) => URL.revokeObjectURL(u));
      setFiles([]);
      setPreviews([]);
      setTitle("");
      setLocation("");
      setDescription("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload eșuat.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/admin/portfolio/${item.id}`, { headers: authHeaders() });
      setItems(items.filter((i) => i.id !== item.id));
      toast.success("Lucrare ștearsă.");
    } catch {
      toast.error("Nu am putut șterge lucrarea.");
    }
  };

  const inputCls =
    "w-full border border-ink/15 bg-surface px-4 py-3 text-sm font-light text-ink placeholder-ink/35 outline-none transition-colors focus:border-gold";
  const labelCls = "kicker mb-2 block";

  return (
    <div data-testid="admin-portfolio-page">
      <h1 className="font-heading text-3xl font-semibold">Portofoliu</h1>
      <p className="mt-2 text-sm text-ink/55">
        Adaugă până la 20 de poze odată — fiecare devine o lucrare separată și apare instant pe pagina publică.
      </p>

      <form onSubmit={submit} className="mt-8 border border-ink/10 bg-surface p-6 lg:p-8" data-testid="portfolio-upload-form">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <div>
            <label className={labelCls}>Imagini * {files.length > 0 && <span className="text-gold">({files.length} selectate)</span>}</label>
            <label
              htmlFor="portfolio-file"
              data-testid="portfolio-file-drop"
              className="flex min-h-48 cursor-pointer items-center justify-center border border-dashed border-ink/25 bg-base p-2 transition-colors hover:border-gold"
            >
              {previews.length > 0 ? (
                <div className={`grid w-full gap-2 ${previews.length > 1 ? "grid-cols-2" : ""}`}>
                  {previews.map((src, i) => (
                    <div key={src} className="group/p relative">
                      <img src={src} alt={`Previzualizare ${i + 1}`} className={`w-full object-cover ${previews.length > 1 ? "h-20" : "h-44"}`} />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); removePreview(i); }}
                        aria-label={`Elimină poza ${i + 1}`}
                        data-testid={`portfolio-preview-remove-${i}`}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover/p:opacity-100"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="flex flex-col items-center gap-2 text-ink/45">
                  <ImagePlus className="h-7 w-7" />
                  <span className="text-xs uppercase tracking-widest">Alege pozele</span>
                  <span className="text-[10px] normal-case tracking-normal text-ink/35">poți selecta mai multe deodată</span>
                </span>
              )}
            </label>
            <input id="portfolio-file" type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid="portfolio-file-input" />
            {previews.length > 0 && (
              <p className="mt-2 text-[11px] text-ink/45">Apasă din nou pe chenar ca să înlocuiești selecția.</p>
            )}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Titlu lucrare {files.length > 1 ? "(comun, opțional)" : "*"}</label>
              <input
                required={files.length <= 1}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex: Poartă culisantă modernă"
                className={inputCls}
                data-testid="portfolio-title-input"
              />
              {files.length > 1 && (
                <p className="mt-1.5 text-[11px] text-ink/45">Lăsat gol, titlul se ia din numele fișierului fiecărei poze.</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Categorie *</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="portfolio-category-select">
                {PORTFOLIO_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Locație</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="ex: Buzău" className={inputCls} data-testid="portfolio-location-input" />
            </div>
            <div>
              <label className={labelCls}>Descriere</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalii scurte (opțional)" className={inputCls} data-testid="portfolio-description-input" />
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={uploading}
          data-testid="portfolio-upload-submit"
          className="btn-sweep mt-6 bg-ink px-8 py-3.5 text-xs font-semibold uppercase tracking-widest text-base transition-colors disabled:opacity-60"
        >
          {uploading
            ? `Se încarcă ${files.length > 1 ? files.length + " poze" : "poza"}...`
            : files.length > 1
              ? `Adaugă ${files.length} lucrări în portofoliu`
              : "Adaugă în portofoliu"}
        </button>
      </form>

      <div className="mt-10">
        {items.length === 0 ? (
          <p className="text-sm text-ink/50" data-testid="portfolio-empty">Nicio lucrare adăugată încă.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div key={item.id} className="group border border-ink/10 bg-surface" data-testid={`portfolio-admin-item-${item.id}`}>
                <div className="h-48 overflow-hidden">
                  <img src={`${API}/files/${item.storage_path}`} alt={item.title} className="h-full w-full object-cover" />
                </div>
                <div className="flex items-start justify-between gap-3 p-4">
                  <div>
                    <h3 className="font-heading text-[16px] text-ink font-semibold">{item.title}</h3>
                    <p className="mt-1 text-xs uppercase tracking-widest text-ink/45">
                      {item.category}{item.location ? ` · ${item.location}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(item)}
                    data-testid={`portfolio-delete-${item.id}`}
                    aria-label="Șterge lucrarea"
                    className="shrink-0 border border-ink/15 p-2 text-ink/50 transition-colors hover:border-red-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
